// JSON-LD graphs emitted into the document head.
//
// Why this exists: AI answer engines (ChatGPT, Claude, Perplexity, Google's AI
// Overviews) read structured data to pull hard facts -- what the product is,
// what it costs, what it runs on -- rather than inferring them from prose. Meta
// tags say how to describe a page; this says what the page is about.
//
// Everything here is rendered into the prerendered HTML by scripts/prerender.js,
// so it's present on first byte for crawlers that never run JavaScript.
//
// Keep the numbers honest. Prices below mirror src/pages/Pricing/PricingSection.jsx;
// if a plan changes there, change it here in the same commit. A price in JSON-LD
// that disagrees with the page is a structured-data violation, and Google will
// drop the rich result rather than pick a side.

import { SITE_URL } from "./seoConfig";
import { helpCategories } from "../data/helpCenter";
import { FAQS as WRITE_EMAILS_FAQS } from "../pages/WriteEmailsWithouttyping/faqs";
import { FAQS as AI_CHAT_PROMPTS_FAQS } from "../pages/vTypingForAI/faqs";

// Stable @id values so the nodes below can reference each other instead of
// repeating themselves. The fragment is arbitrary but must not change once
// crawlers have seen it.
const ORG_ID = `${SITE_URL}/#organization`;
const SITE_ID = `${SITE_URL}/#website`;
const APP_ID = `${SITE_URL}/#software`;

export const ORGANIZATION = {
  "@type": "Organization",
  "@id": ORG_ID,
  name: "Zero Typing AI",
  url: `${SITE_URL}/`,
  logo: {
    "@type": "ImageObject",
    url: `${SITE_URL}/logo512.png`,
    width: 512,
    height: 512,
  },
  description:
    "Zero Typing AI makes voice typing software that turns speech into clean, formatted text inside the apps people already work in.",
};

export const WEBSITE = {
  "@type": "WebSite",
  "@id": SITE_ID,
  name: "Zero Typing AI",
  url: `${SITE_URL}/`,
  publisher: { "@id": ORG_ID },
};

// The product itself. SoftwareApplication is what answer engines look for when
// someone asks "what is X" or "does X run on Mac", and the offers array is what
// they quote when someone asks what it costs.
export const SOFTWARE_APPLICATION = {
  "@type": "SoftwareApplication",
  "@id": APP_ID,
  name: "Zero Typing AI",
  applicationCategory: "ProductivityApplication",
  applicationSubCategory: "Voice typing and dictation software",
  operatingSystem: "Windows, macOS",
  url: `${SITE_URL}/`,
  downloadUrl: `${SITE_URL}/download`,
  publisher: { "@id": ORG_ID },
  description:
    "Zero Typing AI turns speech into clean, formatted text in Google Docs, Gmail, Slack, and AI chat tools. It punctuates, formats, and cleans up dictation as you speak.",
  featureList: [
    "Real-time voice typing in any desktop app",
    "Automatic punctuation and formatting",
    "Personal dictionary for names and jargon",
    "Custom prompts for rewriting and tone",
    "Translate and transcribe audio",
  ],
  offers: [
    {
      "@type": "Offer",
      name: "ZT Basic",
      price: "0",
      priceCurrency: "USD",
      description:
        "Free plan with 15 minutes of audio transcription per week, personal dictionary, and basic prompt selection.",
      url: `${SITE_URL}/pricing`,
    },
    {
      "@type": "Offer",
      name: "ZT Pro (monthly)",
      price: "9.99",
      priceCurrency: "USD",
      description:
        "Unlimited words and audio transcription per week, custom prompt creation, and early access to new features. Billed monthly.",
      url: `${SITE_URL}/pricing`,
    },
    {
      "@type": "Offer",
      name: "ZT Pro (annual)",
      price: "8.00",
      priceCurrency: "USD",
      description:
        "ZT Pro billed annually, which works out to 8.00 USD per month, a 20 percent saving on the monthly price.",
      url: `${SITE_URL}/pricing`,
    },
  ],
};

// ---------------------------------------------------------------------------
// Per-route extras.
//
// Keyed by pathname the same way SEO_BY_PATH is, so a route picks up its own
// nodes without any page component having to know about JSON-LD.
// ---------------------------------------------------------------------------

// Questions people actually ask an assistant about this product. FAQPage is the
// single highest-leverage schema for AI answers: it hands the model a
// question/answer pair it can quote almost verbatim.
const FAQ_PAGE = {
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is Zero Typing AI?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Zero Typing AI is voice typing software for Windows and macOS. You speak, and it types clean, punctuated, formatted text directly into whatever app you're already using, including Google Docs, Gmail, Slack, and AI chat tools.",
      },
    },
    {
      "@type": "Question",
      name: "How much does Zero Typing AI cost?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "There is a free plan, ZT Basic, which includes 15 minutes of audio transcription per week. ZT Pro costs 9.99 USD per month, or 8.00 USD per month when billed annually, and removes the weekly limits.",
      },
    },
    {
      "@type": "Question",
      name: "Does Zero Typing AI work on Mac and Windows?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. Zero Typing AI has a desktop app for both Windows and macOS, and it types into any application on the system rather than only into a browser tab.",
      },
    },
    {
      "@type": "Question",
      name: "How is Zero Typing AI different from built-in dictation?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Built-in dictation transcribes what you say word for word, including filler words and false starts, and leaves you to punctuate and format it. Zero Typing AI cleans the text as you speak: it adds punctuation, breaks paragraphs, and can rewrite in a tone you choose.",
      },
    },
    {
      "@type": "Question",
      name: "Can Zero Typing AI type into Google Docs?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. Zero Typing AI works inside Google Docs along with Gmail, Slack, Notion, and other desktop and browser apps, because it types at the system level rather than through an extension in a single app.",
      },
    },
  ],
};

// A page that explains the product step by step earns the Article treatment,
// which is what gets a page cited by name rather than summarised anonymously.
function article(headline, description, urlPath) {
  return {
    "@type": "Article",
    headline,
    description,
    url: `${SITE_URL}${urlPath}`,
    publisher: { "@id": ORG_ID },
    author: { "@id": ORG_ID },
    isPartOf: { "@id": SITE_ID },
    about: { "@id": APP_ID },
  };
}

// FAQPage from a hand-built article's { question, answer } list, so the schema
// always matches the accordion on the page.
function faqPage(faqs) {
  return {
    "@type": "FAQPage",
    mainEntity: faqs.map(({ question, answer }) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: { "@type": "Answer", text: answer },
    })),
  };
}


// ---------------------------------------------------------------------------
// Help centre structured data, generated from the articles themselves.
//
// This is the highest-value schema on the site. Each article is a real question
// answered in plain language, which is precisely what an answer engine wants to
// quote, and several carry explicit q/a blocks that map straight onto FAQPage.
//
// Emitted per article:
//   TechArticle    - the article itself, with its keywords and section
//   FAQPage        - only when the article actually has faq blocks
//   BreadcrumbList - Help Center > Category > Article, so a citation can show
//                    where in the help centre the answer came from
// ---------------------------------------------------------------------------

// Flatten an article's body blocks into plain prose for articleBody. Structured
// blocks (steps, lists, faq) are unwrapped rather than dropped: an answer engine
// reading articleBody should see the steps, since the steps are usually the
// answer.
function articleBodyText(body = []) {
  const parts = [];

  for (const block of body) {
    if (block.type === "p" || block.type === "h" || block.type === "callout") {
      if (block.text) parts.push(block.text);
    } else if (block.type === "steps" || block.type === "list") {
      parts.push(...(block.items || []));
    } else if (block.type === "faq") {
      for (const item of block.items || []) parts.push(`${item.q} ${item.a}`);
    }
  }

  return parts.join(" ");
}

function faqNodesFor(article) {
  const pairs = (article.body || [])
    .filter((block) => block.type === "faq")
    .flatMap((block) => block.items || []);

  if (pairs.length === 0) return [];

  return [
    {
      "@type": "FAQPage",
      mainEntity: pairs.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
  ];
}

function buildHelpStructuredData() {
  const byPath = {};

  const helpUrl = `${SITE_URL}/help`;

  byPath["/help"] = [
    {
      "@type": "CollectionPage",
      name: "Zero Typing AI Help Center",
      url: helpUrl,
      description:
        "Setup guides, feature explanations, billing answers, and troubleshooting for Zero Typing AI.",
      isPartOf: { "@id": SITE_ID },
      about: { "@id": APP_ID },
      hasPart: helpCategories.map((category) => ({
        "@type": "CollectionPage",
        name: category.name,
        description: category.summary,
        url: `${helpUrl}/topic/${category.slug}`,
      })),
    },
  ];

  for (const category of helpCategories) {
    const categoryUrl = `${helpUrl}/topic/${category.slug}`;

    byPath[`/help/topic/${category.slug}`] = [
      {
        "@type": "CollectionPage",
        name: `${category.name} – Zero Typing AI Help`,
        description: category.summary,
        url: categoryUrl,
        isPartOf: { "@id": SITE_ID },
        hasPart: (category.articles || []).map((article) => ({
          "@type": "TechArticle",
          headline: article.title,
          description: article.description,
          url: `${categoryUrl}/${article.slug}`,
        })),
      },
      breadcrumbs([
        ["Help Center", helpUrl],
        [category.name, categoryUrl],
      ]),
    ];

    for (const article of category.articles || []) {
      const articleUrl = `${categoryUrl}/${article.slug}`;

      byPath[`/help/topic/${category.slug}/${article.slug}`] = [
        {
          "@type": "TechArticle",
          headline: article.title,
          description: article.description,
          url: articleUrl,
          articleSection: category.name,
          keywords: (article.keywords || []).join(", ") || undefined,
          articleBody: articleBodyText(article.body),
          publisher: { "@id": ORG_ID },
          author: { "@id": ORG_ID },
          isPartOf: { "@id": SITE_ID },
          about: { "@id": APP_ID },
        },
        ...faqNodesFor(article),
        breadcrumbs([
          ["Help Center", helpUrl],
          [category.name, categoryUrl],
          [article.title, articleUrl],
        ]),
      ];
    }
  }

  return byPath;
}

function breadcrumbs(trail) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: trail.map(([name, url], i) => ({
      "@type": "ListItem",
      position: i + 1,
      name,
      item: url,
    })),
  };
}

const STRUCTURED_DATA_BY_PATH = {
  "/": [SOFTWARE_APPLICATION, FAQ_PAGE],
  "/pricing": [SOFTWARE_APPLICATION, FAQ_PAGE],
  "/download": [SOFTWARE_APPLICATION],
  "/windows": [SOFTWARE_APPLICATION],
  "/mac": [SOFTWARE_APPLICATION],
  "/demo": [SOFTWARE_APPLICATION],
  "/voice-typing-for-google-docs": [SOFTWARE_APPLICATION, FAQ_PAGE],
  "/voice-typing-for-ai-chat-prompts": [
    SOFTWARE_APPLICATION,
    faqPage(AI_CHAT_PROMPTS_FAQS),
  ],

  "/how-ai-voice-typing-works": [
    article(
      "How AI Voice Typing Actually Works",
      "What happens between speaking into a microphone and seeing a polished paragraph appear on screen.",
      "/how-ai-voice-typing-works"
    ),
  ],
  "/voice-prompting-ai": [
    article(
      "Guide to Dictate Better AI Prompts",
      "The best AI prompts are long, specific, and detailed, which is exactly the kind of thing that is slow to type and fast to say.",
      "/voice-prompting-ai"
    ),
  ],
  "/blog/voice-typing-vs-built-in-dictation": [
    article(
      "Voice Typing vs Built-In Dictation: Windows, Mac, and Google Docs Compared",
      "Every major platform now ships with some form of dictation built in. On paper that should be enough, but most people give up on it within a week.",
      "/blog/voice-typing-vs-built-in-dictation"
    ),
  ],
  "/blog/fastest-way-to-write-emails-without-typing": [
    article(
      "The Fastest Way to Write Emails Without Typing",
      "Most of us spend a surprising part of the workday inside an inbox. Replies, follow-ups, intros, status updates: it adds up, and almost all of it is typed one key at a time.",
      "/blog/fastest-way-to-write-emails-without-typing"
    ),
    faqPage(WRITE_EMAILS_FAQS),
  ],
};

Object.assign(STRUCTURED_DATA_BY_PATH, buildHelpStructuredData());

/**
 * The JSON-LD graph for a route, ready to be stringified into a
 * <script type="application/ld+json"> tag.
 *
 * Organization and WebSite go on every page: a crawler may only ever fetch one
 * URL, and that URL should still identify who publishes the site. Route-specific
 * nodes are appended on top.
 */
export function getStructuredData(pathname = "/") {
  const normalized =
    pathname.length > 1 ? pathname.replace(/\/+$/, "").toLowerCase() : "/";

  const extras = STRUCTURED_DATA_BY_PATH[normalized] || [];

  return {
    "@context": "https://schema.org",
    "@graph": [ORGANIZATION, WEBSITE, ...extras],
  };
}
